import React, { useState, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import InitialAvatar from '../InitialAvatar';
import { getPORefreshHistory } from '../../context/CommonContex';

const PORefresh = ({ onRefresh, lastRefreshData, isLoading, title = 'Purchase Orders' }) => {
    const [refreshCount, setRefreshCount] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshHistory, setRefreshHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const authorizedUsers = [
        'megan.hackettgaddam@gigaflightinc.com',
        'anabella.sengsouvanh@gigaflightinc.com',
        'hannah.mohror@gigaflightinc.com',
        'felicia.watkinsjohnson@gigaflightinc.com',
        'test@gmail.com'
    ];

    const isUserAuthorized = () => {
        let userEmail = localStorage.getItem('userEmail');
        if (!userEmail) return false;

        userEmail = userEmail.trim().toLowerCase();
        const normalizedAuthorized = authorizedUsers.map((user) => user.trim().toLowerCase());
        return normalizedAuthorized.includes(userEmail);
    };

    const loadRefreshHistory = async () => {
        try {
            console.log('📋 [PORefresh] Loading PO refresh history...');
            setLoadingHistory(true);
            const historyData = await getPORefreshHistory();
            console.log('📋 [PORefresh] Refresh history response:', historyData);
            if (historyData && Array.isArray(historyData)) {
                console.log(`📋 [PORefresh] Loaded ${historyData.length} refresh records`);
                setRefreshHistory(historyData);
                setRefreshCount(historyData.length);
            } else {
                console.log('📋 [PORefresh] No history data returned or not an array');
            }
        } catch (error) {
            console.error('❌ [PORefresh] Error loading PO refresh history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = event.target.closest('.refresh-history-dropdown');
            const panel = event.target.closest('.refresh-history-panel');
            if (showHistory && !dropdown && !panel) {
                setShowHistory(false);
            }
        };

        if (showHistory) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showHistory]);

    useEffect(() => {
        console.log('🚀 [PORefresh] Component mounted - calling loadRefreshHistory()');
        loadRefreshHistory();
    }, []);

    const handleRefresh = async () => {
        if (!isUserAuthorized()) {
            alert('You don\'t have access to refresh purchase orders');
            return;
        }

        if (refreshCount >= 10) {
            alert('Maximum refresh limit of 10 reached.');
            return;
        }

        if (isRefreshing) {
            return;
        }

        setIsRefreshing(true);
        setRefreshCount((prev) => prev + 1);

        const refreshUser = localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'Current User';
        const refreshEntry = {
            id: Date.now(),
            timestamp: new Date(),
            user: refreshUser,
            userName: refreshUser,
            status: 'pending'
        };

        setRefreshHistory((prev) => [refreshEntry, ...prev.slice(0, 9)]);

        try {
            await onRefresh();
            setRefreshHistory((prev) => prev.map((entry) => (
                entry.id === refreshEntry.id ? { ...entry, status: 'success' } : entry
            )));
            await loadRefreshHistory();
        } catch (error) {
            console.error('PO refresh failed:', error);
            setRefreshHistory((prev) => prev.map((entry) => (
                entry.id === refreshEntry.id ? { ...entry, status: 'error' } : entry
            )));
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="order-refresh-container">
            <div className="refresh-button-section">
                <Button
                    type="button"
                    variant={isRefreshing ? 'warning' : 'primary'}
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing || isLoading}
                    className="refresh-button"
                    style={{ minWidth: '120px', position: 'relative', overflow: 'hidden' }}
                >
                    {isRefreshing ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Refreshing...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Refresh
                        </>
                    )}

                    {isRefreshing && (
                        <div
                            className="refresh-progress"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                animation: 'refreshShimmer 1.5s infinite'
                            }}
                        ></div>
                    )}
                </Button>

                {refreshCount > 0 && (
                    <Badge bg="info" className="ms-2 refresh-counter" style={{ fontSize: '0.75rem' }}>
                        {refreshCount}
                    </Badge>
                )}

            </div>

            {lastRefreshData && (
                <div className="last-refresh-info">
                    <div className="refresh-status-indicator">
                        <span className="text-muted">
                            <i className="fas fa-clock me-1"></i>
                            {formatTimestamp(lastRefreshData.timestamp)}
                        </span>
                        {lastRefreshData.user && (
                            <span className="text-muted">
                                <i className="fas fa-user me-1"></i>
                                {lastRefreshData.user}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {refreshHistory.length > 0 && (
                <div className="refresh-history-dropdown">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        className="history-button"
                        style={{ fontSize: '0.75rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowHistory(!showHistory);
                            if (!showHistory) {
                                loadRefreshHistory();
                            }
                        }}
                        disabled={loadingHistory}
                        title="Click to view refresh history"
                    >
                        {loadingHistory ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        ) : (
                            <i className="fas fa-history me-1"></i>
                        )}
                        History
                    </Button>

                    <div className={`refresh-history-panel ${showHistory ? 'show' : ''}`} style={{ zIndex: 99999 }} onClick={(e) => e.stopPropagation()}>
                        <div className="history-header">
                            <h6 className="mb-2">{title} Refresh History</h6>
                        </div>
                        <div className="history-list">
                            {refreshHistory.map((entry) => (
                                <div key={entry.id} className="history-item">
                                    <div className="history-item-content">
                                        <div style={{ flexShrink: 0 }}>
                                            <InitialAvatar name={entry.userName || entry.user} size={32} />
                                        </div>
                                        <div className="history-details">
                                            <div className="history-user">{entry.userName || entry.user}</div>
                                            {entry.startTime && (
                                                <div className="history-full-time">
                                                    {formatDate(entry.startTime)} {formatTime(entry.startTime)}
                                                </div>
                                            )}
                                            {entry.durationSeconds && (
                                                <div className="history-duration">
                                                    Duration: {Math.floor(entry.durationSeconds / 60)}m {entry.durationSeconds % 60}s
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .history-item-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 8px;
                }
                .history-details {
                    flex-grow: 1;
                }
                .history-user {
                    font-size: 0.75rem;
                    color: #666;
                }
                .history-full-time,
                .history-duration {
                    font-size: 0.7rem;
                    color: #888;
                }
                .refresh-history-panel {
                    position: absolute;
                    top: 100%;
                    right: -50px;
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 0.375rem;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
                    min-width: 300px;
                    max-height: 400px;
                    overflow-y: auto;
                    display: none;
                    margin-top: 5px;
                }
                .refresh-history-panel.show {
                    display: block;
                }
                @keyframes refreshShimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
            `}</style>
        </div>
    );
};

export default PORefresh;
